import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useSnackbar } from 'notistack';
import { useUserState } from 'src/contexts/UserContext';
import { usePlaylists } from './usePlaylists';
import { usePlaylistStatus } from './usePlaylistStatus';
import { usePlaylistMutations } from './usePlaylistMutations';
import type { PlaylistContentType } from './api';
import styled from 'styled-components';

const IconBtn = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background: rgba(100, 100, 100, 0.2);
  backdrop-filter: blur(2px) invert(20%) brightness(120%);
  color: #ffffff;
  z-index: 1;
  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

type WatchLaterButtonProps = {
  contentId: string;
  contentType: PlaylistContentType;
  variant?: 'icon' | 'labeled';
};

export function WatchLaterButton(props: WatchLaterButtonProps) {
  if (props.contentType === 'challenge') {
    return null;
  }
  return <WatchLaterButtonInner {...props} />;
}

function WatchLaterButtonInner({
  contentId,
  contentType,
  variant = 'icon',
}: WatchLaterButtonProps) {
  const { isLoggedIn } = useUserState();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { data: playlists } = usePlaylists(isLoggedIn);
  const watchLaterId = playlists?.find((p) => p.playlistType === 'watch_later')?.id;
  const { data: status, isLoading: statusLoading } = usePlaylistStatus(
    contentId,
    contentType,
    isLoggedIn,
  );
  const { toggleWatchLater } = usePlaylistMutations();
  const [local, setLocal] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    setLocal(undefined);
  }, [contentId, contentType, status?.inWatchLater]);

  const serverIn = status?.inWatchLater ?? false;
  const effective = local ?? serverIn;
  const busy = toggleWatchLater.isLoading;

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isLoggedIn) {
        history.push('/login');
        return;
      }
      if (!watchLaterId || statusLoading) return;
      const next = !effective;
      setLocal(next);
      toggleWatchLater.mutate(
        {
          playlistId: watchLaterId,
          contentId,
          contentType,
          shouldAdd: next,
          itemId: status?.watchLaterItemId,
        },
        {
          onSuccess: () => {
            enqueueSnackbar(
              next ? 'Dodano do: Na później' : 'Usunięto z: Na później',
              { variant: 'success' },
            );
            setLocal(undefined);
          },
          onError: () => setLocal(undefined),
        },
      );
    },
    [
      isLoggedIn,
      history,
      watchLaterId,
      statusLoading,
      effective,
      toggleWatchLater,
      contentId,
      contentType,
      status?.watchLaterItemId,
      enqueueSnackbar,
    ],
  );

  if (variant === 'labeled') {
    return (
      <Tooltip title={effective ? 'Usuń z „Na później”' : 'Zapisz na później'}>
        <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={effective ? <BookmarkIcon /> : <BookmarkBorderIcon />}
            onClick={onClick}
            disabled={!isLoggedIn ? false : !watchLaterId || busy}
          >
            {effective ? 'W „Na później”' : 'Na później'}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={effective ? 'Usuń z „Na później”' : 'Na później'}>
      <IconBtn
        type="button"
        $active={effective}
        onClick={onClick}
        disabled={!isLoggedIn ? false : !watchLaterId || busy}
      >
        {effective ? (
          <BookmarkIcon sx={{ fontSize: 22, color: 'inherit' }} />
        ) : (
          <BookmarkBorderIcon sx={{ fontSize: 22, color: 'inherit' }} />
        )}
      </IconBtn>
    </Tooltip>
  );
}
